import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const prefixCachingComponent: ComponentDef = {
  id: 'prefix-caching',
  name: 'Prefix Caching',
  description: 'Hash-based KV cache reuse: shared prefixes (system prompt, few-shot examples) are cached in a radix tree and reused across requests — eliminating redundant prefill compute.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'prefix_len', label: 'Shared prefix length', type: 'number', default: 512, min: 1, max: 8192, step: 1 },
    { name: 'suffix_len', label: 'New suffix length', type: 'number', default: 128, min: 1, max: 4096, step: 1 },
    { name: 'block_size', label: 'KV block size (tokens)', type: 'number', default: 16, min: 1, max: 256, step: 1 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const prefix_len = Number(params.prefix_len);
    const suffix_len = Number(params.suffix_len);
    const block_size = Number(params.block_size);
    const n_prefix_blocks = Math.ceil(prefix_len / block_size);
    const n_suffix_blocks = Math.ceil(suffix_len / block_size);

    const g = new GraphBuilder();

    // ── Incoming request: system prompt + user suffix ──
    g.input('system_prompt', [prefix_len, d_model]);
    g.input('user_suffix', [suffix_len, d_model]);

    // ── Radix tree lookup: hash prefix tokens to find cached KV blocks ──
    g.activation('prefix_hash', 'system_prompt', `hash(tokens[0:${prefix_len}])`);
    g.router('radix_lookup', 'prefix_hash', [n_prefix_blocks, 1]);  // HIT or MISS per block

    // ── Cache HIT path: reuse prefix KV blocks directly from radix tree ──
    g.cache('cached_kv_blocks', 'radix_lookup', [n_prefix_blocks, block_size, d_model]);
    // Ref-count incremented; blocks are copy-on-write if another request shares them
    g.scale('ref_count_incr', 'cached_kv_blocks', 'ref_count++  (CoW on write)');

    // ── Cache MISS path: compute KV for new suffix only ──
    g.linear('suffix_qkv', 'user_suffix', [d_model, d_model * 3]);
    g.reshape('suffix_kv', 'suffix_qkv', [suffix_len, d_model * 2]);
    g.cache('new_kv_blocks', 'suffix_kv', [n_suffix_blocks, block_size, d_model]);

    // ── Insert new suffix blocks into radix tree ──
    g.activation('insert_radix', 'new_kv_blocks', 'radix_insert(hash, kv_blocks)');

    // ── Merge: full KV = cached prefix KV + new suffix KV ──
    g.concat('full_kv', ['ref_count_incr', 'new_kv_blocks'], [prefix_len + suffix_len, d_model]);

    // ── Attention over full context (suffix tokens query full KV) ──
    g.linear('q_proj', 'user_suffix', [d_model, d_model]);
    g.transpose('kv_T', 'full_kv', [d_model, prefix_len + suffix_len], 'K^T');
    g.matmul('attn_scores', 'q_proj', 'kv_T', [suffix_len, prefix_len + suffix_len]);
    g.softmax('attn_w', 'attn_scores', -1);
    g.matmul('attn_out', 'attn_w', 'full_kv', [suffix_len, d_model]);
    g.linear('out_proj', 'attn_out', [d_model, d_model]);

    // ── Eviction policy: LRU / ref-count based ──
    g.activation('evict_check', 'full_kv', 'LRU evict if ref_count == 0');

    g.output('out', 'out_proj');
    return g.build();
  }
};
